import { useState } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { Send, Inbox, CheckCheck, Eye, XCircle, MessagesSquare, Users, Bot, Activity, Gauge } from "lucide-react";
import { PageHeader, StatCard, card, Loading, ErrorState, DemoBanner, Badge, DemoBadge, Empty } from "@/components/common";
import { useFetch, fmtTime, fmtPhone } from "@/lib/api";

export function RangeFilter({ value, onChange, options = [7, 14, 30, 90] }) {
  return (
    <div className="inline-flex p-1 rounded-lg bg-white border border-slate-200" data-testid="date-range-filter">
      {options.map((d) => (
        <button key={d} data-testid={`range-${d}d`} onClick={() => onChange(d)} className={`px-3 h-7 rounded-md text-xs font-semibold transition-colors ${value === d ? "bg-[#128C7E] text-white" : "text-slate-500 hover:text-slate-800"}`}>{d}d</button>
      ))}
    </div>
  );
}

export function VolumeChart({ series }) {
  const data = (series || []).map((s) => ({ ...s, date: s.date.slice(5) }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <AreaChart data={data} margin={{ left: -20, right: 8, top: 8 }}>
        <defs>
          <linearGradient id="gOut" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#25D366" stopOpacity={0.35} /><stop offset="100%" stopColor="#25D366" stopOpacity={0} /></linearGradient>
          <linearGradient id="gIn" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.25} /><stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} /></linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
        <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Area type="monotone" dataKey="outgoing" stroke="#128C7E" fill="url(#gOut)" strokeWidth={2} />
        <Area type="monotone" dataKey="incoming" stroke="#0ea5e9" fill="url(#gIn)" strokeWidth={2} />
        <Area type="monotone" dataKey="failed" stroke="#f43f5e" fill="transparent" strokeWidth={1.5} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default function Overview() {
  const [days, setDays] = useState(7);
  const { data, loading, error, reload } = useFetch(`/overview?days=${days}`, { interval: 15000 });
  const s = data?.stats;
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto space-y-6">
      <PageHeader title="Overview" subtitle="Live messaging health for your workspace, computed from your database records."><RangeFilter value={days} onChange={setDays} /></PageHeader>
      {error && <ErrorState message={error} onRetry={reload} />}
      {loading && !data ? <Loading /> : data && (<>
        <DemoBanner show={data.demo_mode} />
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          <StatCard testid="stat-sent" label="Messages sent" value={s.sent} icon={Send} />
          <StatCard testid="stat-incoming" label="Incoming" value={s.incoming} icon={Inbox} tone="blue" />
          <StatCard testid="stat-delivered" label="Delivered" value={s.delivered} icon={CheckCheck} />
          <StatCard testid="stat-read" label="Read" value={s.read} icon={Eye} tone="blue" />
          <StatCard testid="stat-failed" label="Failed" value={s.failed} icon={XCircle} tone="red" />
          <StatCard testid="stat-active-conversations" label="Active conversations" value={s.active_conversations} icon={MessagesSquare} tone="amber" />
          <StatCard testid="stat-contacts" label="Total contacts" value={s.total_contacts} icon={Users} tone="slate" />
          <StatCard testid="stat-ai-resolved" label="AI-resolved" value={s.ai_resolved} icon={Bot} tone="violet" />
          <StatCard testid="stat-api-usage" label="API requests" value={s.api_usage} icon={Activity} tone="slate" sub={`${s.api_errors} errors`} />
          <StatCard testid="stat-success-rate" label="Delivery success" value={`${s.delivery_success_rate}%`} icon={Gauge} />
        </div>
        <div className={`${card} p-5`}>
          <div className="flex items-center justify-between mb-2"><h3 className="font-bold text-slate-800">Message volume</h3><span className="text-xs text-slate-400">Last {days} days</span></div>
          <VolumeChart series={s.series} />
        </div>
        <div className="grid lg:grid-cols-3 gap-4">
          <div className={`${card} p-5 overflow-hidden`} data-testid="recent-conversations">
            <div className="flex items-center justify-between mb-3"><h3 className="font-bold text-slate-800">Recent conversations</h3><Link to="/inbox" className="text-xs font-semibold text-[#128C7E]">Open inbox</Link></div>
            {data.recent_conversations.length === 0 ? <Empty title="No conversations yet" /> : data.recent_conversations.map((c) => (
              <Link to={`/inbox?c=${c.id}`} key={c.id} className="flex items-center gap-3 py-2.5 border-b last:border-0 border-slate-100 hover:bg-slate-50 -mx-2 px-2 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-emerald-100 text-[#075E54] text-xs font-bold flex items-center justify-center">{(c.contact_name || "?")[0]}</div>
                <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate">{c.contact_name || fmtPhone(c.contact_phone)}</div><div className="text-xs text-slate-500 truncate">{c.last_message}</div></div>
                <div className="flex flex-col items-end gap-1"><Badge>{c.status}</Badge>{c.is_demo && <DemoBadge />}</div>
              </Link>
            ))}
          </div>
          <div className={`${card} p-5`} data-testid="recent-webhook-events">
            <div className="flex items-center justify-between mb-3"><h3 className="font-bold text-slate-800">Recent webhook events</h3><Link to="/webhooks" className="text-xs font-semibold text-[#128C7E]">View all</Link></div>
            {data.recent_webhook_events.length === 0 ? <Empty title="No webhook events" text="Inbound Meta events and outbound deliveries appear here." /> : data.recent_webhook_events.map((e) => (
              <div key={e.id} className="flex items-center justify-between py-2.5 border-b last:border-0 border-slate-100">
                <div className="min-w-0"><div className="text-sm font-mono truncate">{e.type}</div><div className="text-[11px] text-slate-400">{e.direction} · {fmtTime(e.created_at)}</div></div><Badge>{e.status}</Badge>
              </div>
            ))}
          </div>
          <div className={`${card} p-5`} data-testid="recent-api-errors">
            <div className="flex items-center justify-between mb-3"><h3 className="font-bold text-slate-800">Recent API errors</h3><Link to="/playground" className="text-xs font-semibold text-[#128C7E]">Playground</Link></div>
            {data.recent_api_errors.length === 0 ? <Empty title="No API errors" text="Failed /api/v1 requests will be listed here." /> : data.recent_api_errors.map((e) => (
              <div key={e.id} className="flex items-center justify-between py-2.5 border-b last:border-0 border-slate-100">
                <div className="min-w-0"><div className="text-sm font-mono truncate">{e.method} {e.path}</div><div className="text-[11px] text-slate-400">{e.key_name} · {fmtTime(e.created_at)}</div></div><Badge color="red">{e.status_code}</Badge>
              </div>
            ))}
          </div>
        </div>
      </>)}
    </div>
  );
}
